import { takeLatest, put, call, takeEvery, all, delay } from "redux-saga/effects";

import {
	getAuthenticationKeyStart,
	getAuthenticationKeyComplete,
	getAuthenticationKeyFail,
	getPaymentAcceptanceComplete,
	getPaymentAcceptanceFail,
	getPaymentAcceptanceStart,
	postCashPayComplete,
	postCashPayFail,
	postCashPayStart,
	postCheckPayStart,
	postCheckPayComplete,
	postCheckPayFail,
	postCreditPayComplete,
	postCreditPayFail,
	postCreditPayStart,
	postHostedCaptureStart,
	postHostedCaptureComplete,
	postHostedCaptureFail,
	postDebitPayComplete,
	postDebitPayFail,
	postDebitPayStart,
	postEmvPayStart,
	postEmvPayComplete,
	postEmvPayFail,
	postSwipedPayStart,
	postSwipedPayComplete,
	postSwipedPayFail,
	postGiftPayStart,
	postGiftPayComplete,
	postGiftPayFail,
	postRefundsStart,
	postRefundsComplete,
	postRefundsFail,
	postSingleRefundComplete,
	postSingleRefundFail,
	getRefundSummaryComplete,
	getRefundSummaryFail,
	getRefundSummaryStart,
	postInterCardPayStart,
	postStripeEmvPayStart,
	postStripeEmvPayComplete,
	postStripeEmvPayFail,
	postRefundInCashComplete,
	postRefundInCashFail,
	postRefundInCashStart,
} from "./payments.slice";

import PaymentsAPI from "./payments.api";
import {
	IGetRefundSummaryPayload,
	IPostRefundData,
	IPostRefundInCashPayload,
	IPostRefundResponse,
	IPostRefundsPayload,
} from "./reducers/refund.reducer";

// Payment failure messages are rendered as a React child in PaymentErrorModal, and the
// method forms react only to a truthy message: it must be a non-empty string.
export const toPaymentErrorMessage = (error: any): string =>
	error?.response?.data?.results?.message || error?.message || String(error);

export function* getAuthenticationKey({ payload }) {
	try {
		const { isToken, paymentAmount } = payload;

		if (isToken) {
			const { data } = yield call(PaymentsAPI.getAuthenticationKeyToken);
			yield put(getAuthenticationKeyComplete(data));
		} else {
			const { data } = yield call(
				PaymentsAPI.getAuthenticationKeyAuthorization,
				paymentAmount
			);
			yield put(getAuthenticationKeyComplete(data));
		}
	} catch (e: any) {
		yield put(getAuthenticationKeyFail(e.message));
	}
}

export function* getPaymentAcceptance() {
	try {
		const response = yield call(PaymentsAPI.getPaymentAcceptance);

		if (response.data.ok) {
			yield put(getPaymentAcceptanceComplete({ data: response.data }));
		} else {
			yield put(
				getPaymentAcceptanceFail({
					error: Object.values(response.data.errors)[0],
				})
			);
		}
	} catch (error: any) {
		yield put(getPaymentAcceptanceFail(error));
	}
}

export function* postCashPay({ payload }) {
	const { paymentAcceptanceId, locationContext, amount, orderId, notes, stationId } =
		payload;
	try {
		const response = yield call(
			PaymentsAPI.postCashPay,
			orderId,
			paymentAcceptanceId,
			locationContext,
			amount,
			notes,
			stationId
		);

		if (response.data.ok) {
			yield put(postCashPayComplete({ data: response.data }));
		} else {
			yield put(
				postCashPayFail({
					error: Object.values(response.data.errors)[0],
				})
			);
		}
	} catch (error: any) {
		yield put(postCashPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postCheckPay({ payload }) {
	const {
		paymentAcceptanceId,
		locationContext,
		amount,
		orderId,
		notes,
		nameOnCheck,
		checkNumber,
		stationId,
	} = payload;

	try {
		const response = yield call(
			PaymentsAPI.postCheckPay,
			orderId,
			paymentAcceptanceId,
			locationContext,
			amount,
			nameOnCheck,
			checkNumber,
			notes,
			stationId
		);

		if (response.data.ok) {
			yield put(postCheckPayComplete({ data: response.data }));
		} else {
			yield put(
				postCheckPayFail({
					error: Object.values(response.data.errors)[0],
				})
			);
		}
	} catch (error: any) {
		yield put(postCheckPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postHostedCapture({ payload }) {
	const {
		paymentAcceptanceId,
		locationContext,
		amount,
		orderId,
		transactionId,
		notes,
		stationId,
		isStripePayment,
		token,
		paymentModuleId,
		paymentIntentId,
	} = payload;
	try {
		let response;
		if (isStripePayment) {
			if (paymentIntentId?.startsWith("pi_")) {
				response = yield call(
					PaymentsAPI.postStripeHostedCaptureWithIntent,
					orderId,
					paymentAcceptanceId,
					locationContext,
					amount,
					notes,
					stationId,
					paymentModuleId,
					paymentIntentId
				);
			} else {
				response = yield call(
					PaymentsAPI.postStripeHostedCapture,
					orderId,
					paymentAcceptanceId,
					locationContext,
					amount,
					notes,
					stationId,
					token,
					paymentModuleId
				);
			}
		} else {
			response = yield call(
				PaymentsAPI.postFSHostedCapture,
				orderId,
				transactionId,
				paymentAcceptanceId,
				locationContext,
				amount,
				notes,
				stationId
			);
		}
		if (response.data.ok) {
			yield put(postHostedCaptureComplete({ data: response.data }));
		} else {
			yield put(postHostedCaptureFail(response.data.results.message));
		}
	} catch (error: any) {
		yield put(postHostedCaptureFail(toPaymentErrorMessage(error)));
	}
}

export function* postCreditPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postCreditPay, payload);

		if (response.data.ok) {
			yield put(postCreditPayComplete({ data: response.data }));
		} else {
			yield put(
				postCreditPayFail({
					error: Object.values(response.data.errors)[0],
				})
			);
		}
	} catch (error: any) {
		yield put(postCreditPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postDebitPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postDebitPay, payload);

		if (response.data.ok) {
			yield put(postDebitPayComplete({ data: response.data }));
		} else {
			yield put(
				postDebitPayFail({
					error: Object.values(response.data.errors)[0],
				})
			);
		}
	} catch (error: any) {
		yield put(postDebitPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postEmvPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postEmvPay, payload);

		if (response.data.ok) {
			yield put(postEmvPayComplete({ data: response.data }));
		} else {
			yield put(postEmvPayFail(response.data.results.message));
		}
	} catch (error: any) {
		yield put(postEmvPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postStripeEmvPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postStripeEmvPay, payload);

		if (response.data.ok) {
			yield put(postStripeEmvPayComplete({ data: response.data }));
		} else {
			yield put(postStripeEmvPayFail(response.data.results.message));
		}
	} catch (error: any) {
		yield put(postStripeEmvPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postSwipedPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postSwipedPay, payload);

		if (response.data.ok) {
			yield put(postSwipedPayComplete({ data: response.data }));
		} else {
			yield put(postSwipedPayFail(response.data.results.message));
		}
	} catch (error: any) {
		yield put(postSwipedPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postGiftCardPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postGiftCardPay, payload);

		if (response.data.ok) {
			yield put(postGiftPayComplete({ data: response.data }));
		} else {
			yield put(postGiftPayFail(response.data.results.message));
		}
	} catch (error: any) {
		yield put(postGiftPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postInterCardPay({ payload }) {
	try {
		const response = yield call(PaymentsAPI.postInterCardPay, payload);

		if (response.data.ok) {
			yield put(postGiftPayComplete({ data: response.data }));
		} else {
			yield put(postGiftPayFail(response.data.results.message));
		}
	} catch (error: any) {
		yield put(postGiftPayFail(toPaymentErrorMessage(error)));
	}
}

export function* postRefund({
	payload,
}: {
	payload: { orderId: number; data: IPostRefundData };
}) {
	const response = yield call(PaymentsAPI.posRefund, payload);

	if (response.data.ok) {
		yield put(postSingleRefundComplete({ data: response.data }));
	} else {
		throw new Error(response.data.message);
	}
}

// export function* watchPostRefunds({ payload }: { payload: IPostRefundsPayload }) {
// 	try {
// 		yield all(
// 			payload.transactions.map((data) =>
// 				call(postRefund, { payload: { orderId: payload.orderId, data } })
// 			)
// 		);
// 		yield put(postRefundsComplete({}));
// 	} catch (error: any) {
// 		yield put(postRefundsFail(error.message));
// 		yield put(postSingleRefundFail(error.message));
// 	}
// }

export function* watchPostRefunds({ payload }: { payload: IPostRefundsPayload }) {
	const result: IPostRefundResponse = {
		results: [],
		success: false,
	};

	try {
		for (const data of payload.transactions) {
			try {
				const response: { data: { ok: boolean; message: string } } = yield call(
					PaymentsAPI.posRefund,
					{
						orderId: payload.orderId,
						data,
					}
				);

				if (response.data.ok) {
					result.results.push({
						transactionId: data.transactionId,
						status: "success",
						amount: data.refundAmount,
						isCustomAmountRefund: data.isCustomAmountRefund,
						last4Digits: data.last4Digits,
						isCash: data.isCash,
					});
				} else {
					result.results.push({
						transactionId: data.transactionId,
						status: "failed",
						amount: data.refundAmount,
						isCustomAmountRefund: data.isCustomAmountRefund,
						error: response.data.message || "Unknown error",
						last4Digits: data.last4Digits,
						isCash: data.isCash,
					});
				}
			} catch (error: any) {
				result.results.push({
					transactionId: data.transactionId,
					status: "failed",
					amount: data.refundAmount,
					isCustomAmountRefund: data.isCustomAmountRefund,
					error: error.message || "Unknown error",
					last4Digits: data.last4Digits,
					isCash: data.isCash,
				});
			}
			yield delay(500); // waits 500ms between each request
		}

		result.success = result.results.every((item) => item.status === "success");
		yield put(postRefundsComplete(result));
	} catch (error: any) {
		result.success = false;
		yield put(postRefundsFail(result));
	}
}

export function* GetrefundSummary({
	payload,
}: {
	payload: IGetRefundSummaryPayload;
}): any {
	try {
		const { orderId, request } = payload;
		const response = yield call(PaymentsAPI.GetRefundSumarry, request, orderId);

		yield put(getRefundSummaryComplete(response));
	} catch (error: any) {
		yield put(getRefundSummaryFail(payload));
	}
}
export function* PostRefundInCash({
	payload,
}: {
	payload: IPostRefundInCashPayload;
}): any {
	try {
		const response = yield call(PaymentsAPI.posRefundInCash, payload);

		if (response.data.ok) {
			yield put(postRefundInCashComplete({ data: response.data }));
		} else {
			yield put(postRefundInCashFail(payload));
		}
	} catch (error: any) {
		yield put(postRefundInCashFail(error.message));
	}
}

// Root Sagas
export function* watchPayments() {
	return [
		yield takeLatest(getAuthenticationKeyStart, getAuthenticationKey),
		yield takeLatest(getPaymentAcceptanceStart, getPaymentAcceptance),
		yield takeLatest(postCashPayStart, postCashPay),
		yield takeLatest(postCheckPayStart, postCheckPay),
		yield takeLatest(postHostedCaptureStart, postHostedCapture),
		yield takeLatest(postCreditPayStart, postCreditPay),
		yield takeLatest(postDebitPayStart, postDebitPay),
		yield takeLatest(postEmvPayStart, postEmvPay),
		yield takeLatest(postStripeEmvPayStart, postStripeEmvPay),
		yield takeLatest(postSwipedPayStart, postSwipedPay),
		yield takeLatest(postGiftPayStart, postGiftCardPay),
		yield takeLatest(postInterCardPayStart, postInterCardPay),
		yield takeEvery(postRefundsStart, watchPostRefunds),
		yield takeLatest(getRefundSummaryStart, GetrefundSummary),
		yield takeLatest(postRefundInCashStart, PostRefundInCash),
	];
}
